"use client"

import { useState } from "react"
import Image from "next/image"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Search, Loader2, ExternalLink, Twitter, Share2, Code, Copy, Check } from "lucide-react"
import { toast } from "sonner"
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard"

interface MetaTagsResult {
  url: string
  title?: string
  description?: string
  canonical?: string
  favicon?: string
  openGraph: {
    title?: string
    description?: string
    image?: string
    url?: string
    type?: string
    siteName?: string
  }
  twitter: {
    card?: string
    title?: string
    description?: string
    image?: string
    site?: string
    creator?: string
  }
  meta: Array<{
    name?: string
    property?: string
    content?: string
    charset?: string
    httpEquiv?: string
  }>
  timing: number
}

export function MetaTagsAnalyzer() {
  const [url, setUrl] = useState("")
  const [result, setResult] = useState<MetaTagsResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { copy, isCopied } = useCopyToClipboard()

  const handleAnalyze = async () => {
    if (!url.trim()) {
      toast.error("Please enter a URL")
      return
    }

    let normalizedUrl = url.trim()
    if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
      normalizedUrl = "https://" + normalizedUrl
    }

    setLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await fetch("/api/tools/meta-tags", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: normalizedUrl }),
      })

      const data = await response.json()

      if (!data.success) {
        setError(data.error?.message || "Failed to analyze meta tags")
        toast.error(data.error?.message || "Failed to analyze meta tags")
        return
      }

      setResult(data.data)
      toast.success("Meta tags analyzed successfully")
    } catch {
      setError("Failed to analyze URL")
      toast.error("Failed to analyze URL")
    } finally {
      setLoading(false)
    }
  }

  const copyValue = (value: string, key: string) => {
    copy(value, key)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAnalyze()
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Meta Tags Analyzer
          </CardTitle>
          <CardDescription>
            Analyze Open Graph, Twitter Cards, and other meta tags from any URL
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="url">URL</Label>
            <div className="flex gap-2">
              <Input
                id="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1"
              />
              <Button onClick={handleAnalyze} disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-2">Analyze</span>
              </Button>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <>
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Basic Information</CardTitle>
                <Badge variant="outline">{result.timing}ms</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {result.title && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Title</Label>
                  <p className="font-medium">{result.title}</p>
                </div>
              )}
              {result.description && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="text-sm">{result.description}</p>
                </div>
              )}
              {result.canonical && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Canonical URL</Label>
                  <a
                    href={result.canonical}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    {result.canonical}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {result.favicon && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Favicon</Label>
                  <div className="flex items-center gap-2">
                    <Image src={result.favicon} alt="Analyzed site favicon" width={16} height={16} unoptimized className="w-4 h-4" />
                    <code className="text-xs">{result.favicon}</code>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Social Previews */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Social Media Previews</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="og">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="og" className="flex items-center gap-2">
                    <Share2 className="h-4 w-4" />
                    Open Graph
                  </TabsTrigger>
                  <TabsTrigger value="twitter" className="flex items-center gap-2">
                    <Twitter className="h-4 w-4" />
                    Twitter Card
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="og" className="space-y-4 mt-4">
                  {/* OG Preview Card */}
                  <div className="border rounded-lg overflow-hidden max-w-md">
                    {result.openGraph.image && (
                      <div className="aspect-video bg-muted relative">
                        <Image
                          src={result.openGraph.image}
                          alt="Open Graph social preview"
                          fill
                          unoptimized
                          sizes="(max-width: 768px) 100vw, 448px"
                          className="object-cover"
                        />
                      </div>
                    )}
                    <div className="p-3 bg-muted/30">
                      <p className="text-xs text-muted-foreground uppercase">
                        {result.openGraph.siteName || new URL(result.url).hostname}
                      </p>
                      <p className="font-semibold line-clamp-2">
                        {result.openGraph.title || result.title || "No title"}
                      </p>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {result.openGraph.description || result.description || "No description"}
                      </p>
                    </div>
                  </div>

                  {/* OG Tags */}
                  <div className="space-y-2">
                    {Object.entries(result.openGraph).map(([key, value]) =>
                      value ? (
                        <div
                          key={key}
                          className="flex items-center justify-between p-2 rounded bg-muted/50"
                        >
                          <span className="text-sm">
                            <span className="text-muted-foreground">og:{key}:</span>{" "}
                            {value}
                          </span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => copyValue(value, `og-${key}`)}
                          >
                            {isCopied(`og-${key}`) ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : null
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="twitter" className="space-y-4 mt-4">
                  {/* Twitter Preview Card */}
                  <div className="border rounded-lg overflow-hidden max-w-md">
                    {result.twitter.image && (
                      <div className="aspect-video bg-muted relative">
                        <Image
                          src={result.twitter.image}
                          alt="Twitter card preview"
                          fill
                          unoptimized
                          sizes="(max-width: 768px) 100vw, 448px"
                          className="object-cover"
                        />
                      </div>
                    )}
                    <div className="p-3 bg-muted/30">
                      <p className="font-semibold line-clamp-2">
                        {result.twitter.title || result.title || "No title"}
                      </p>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {result.twitter.description || result.description || "No description"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new URL(result.url).hostname}
                      </p>
                    </div>
                  </div>

                  {/* Twitter Tags */}
                  <div className="space-y-2">
                    {Object.entries(result.twitter).map(([key, value]) =>
                      value ? (
                        <div
                          key={key}
                          className="flex items-center justify-between p-2 rounded bg-muted/50"
                        >
                          <span className="text-sm">
                            <span className="text-muted-foreground">twitter:{key}:</span>{" "}
                            {value}
                          </span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => copyValue(value, `tw-${key}`)}
                          >
                            {isCopied(`tw-${key}`) ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : null
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* All Meta Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Code className="h-5 w-5" />
                All Meta Tags ({result.meta.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {result.meta.map((tag, idx) => (
                  <div
                    key={idx}
                    className="p-2 rounded bg-muted/50 font-mono text-xs break-all"
                  >
                    &lt;meta{" "}
                    {tag.name && <span className="text-blue-500">name=&quot;{tag.name}&quot;</span>}
                    {tag.property && (
                      <span className="text-purple-500">property=&quot;{tag.property}&quot;</span>
                    )}
                    {tag.content && (
                      <span className="text-green-500"> content=&quot;{tag.content}&quot;</span>
                    )}
                    {tag.charset && (
                      <span className="text-orange-500">charset=&quot;{tag.charset}&quot;</span>
                    )}
                    {tag.httpEquiv && (
                      <span className="text-red-500">http-equiv=&quot;{tag.httpEquiv}&quot;</span>
                    )}
                    /&gt;
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
